import { useEffect, useState } from "react";
import { ShieldCheck, Trash2, Plus, Settings2, Info } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const VIOLET = "#6D5DF6";
const REGIMES = { micro: "Micro-entreprise", ei_reel: "Entreprise individuelle (réel)", eurl: "EURL", sasu: "SASU", autre: "Autre" };
const inp = "border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs";
const frMonth = (k) => new Date(k + "-15").toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

export default function UrssafTab({ from, to }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(false);
  const [f, setF] = useState(null);
  const [entry, setEntry] = useState({ period: to.slice(0, 7), declared: "", paid: "", note: "" });
  const load = () => {
    api.get("/pilotage/urssaf", { params: { frm: from, to } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/pilotage/settings").then((r) => setF({
      regime: r.data.regime, urssaf_periodicite: r.data.urssaf_periodicite,
      impots_provision_mensuelle: r.data.impots_provision_mensuelle,
      taux: (r.data.urssaf_rates?.slice(-1)[0]?.rate) ?? 25.6, eff: new Date().toISOString().slice(0, 10),
    })).catch(() => {});
  };
  useEffect(() => { setData(null); load(); }, [from, to]); // eslint-disable-line
  if (!data || !f) return <p className="py-10 text-center text-xs text-neutral-400">Chargement…</p>;

  const saveCfg = async () => {
    try {
      await api.put("/pilotage/settings", {
        regime: f.regime, urssaf_periodicite: f.urssaf_periodicite,
        impots_provision_mensuelle: +f.impots_provision_mensuelle || 0,
        urssaf_rates: [{ rate: +f.taux || 0, effective_from: f.eff }],
      });
      toast.success("Paramètres enregistrés — le nouveau taux s'applique à partir de sa date d'effet");
      setCfg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addEntry = async () => {
    if (!(+entry.declared > 0 || +entry.paid > 0)) { toast.error("Renseignez un montant déclaré ou payé"); return; }
    try {
      await api.post("/pilotage/urssaf/entries", { ...entry, declared: +entry.declared || 0, paid: +entry.paid || 0 });
      toast.success("Cotisation enregistrée"); setEntry({ period: to.slice(0, 7), declared: "", paid: "", note: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/pilotage/urssaf/entries/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const cards = [
    ["Taux applicable", `${data.taux} %`, data.regime === "micro" ? "micro-entreprise" : REGIMES[data.regime]],
    ["Base de calcul (CA encaissé)", eur(data.base_encaissee), "paiements réellement reçus"],
    ["Cotisations estimées", eur(data.estime), data.calcul_auto ? "calcul automatique" : "saisie manuelle"],
    ["Réellement déclarées", eur(data.declare), "période consultée"],
    ["Réellement payées", eur(data.paye), "période consultée"],
    ["Solde restant à payer", eur(data.solde_restant), `prochaine échéance : ${new Date(data.prochaine_echeance + "T12:00:00").toLocaleDateString("fr-FR")}`],
  ];
  return (
    <div className="space-y-4" data-testid="urssaf-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-neutral-500 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />
          Régime : <b>{REGIMES[data.regime]}</b> · déclaration {data.periodicite} — l'estimation suit les encaissements réels et le taux à date d'effet.</p>
        <button onClick={() => setCfg(!cfg)} className="flex items-center gap-1.5 text-[11px] font-medium border border-neutral-200 rounded-lg px-3 py-1.5 hover:bg-neutral-50" data-testid="urssaf-config-btn">
          <Settings2 className="w-3.5 h-3.5" /> {cfg ? "Fermer" : "Configurer le régime"}
        </button>
      </div>
      {cfg && (
        <div className="bg-white border border-neutral-100 rounded-2xl p-4 grid grid-cols-2 lg:grid-cols-4 gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="urssaf-config">
          <label className="text-[11px] text-neutral-500">Régime juridique
            <select value={f.regime} onChange={(e) => setF({ ...f, regime: e.target.value })} className={`${inp} w-full mt-1`} data-testid="urssaf-regime">
              {Object.entries(REGIMES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="text-[11px] text-neutral-500">Taux social (%){f.regime !== "micro" && " — libre, sinon saisie manuelle"}
            <Input type="number" step="0.01" min="0" value={f.taux} onChange={(e) => setF({ ...f, taux: e.target.value })} className="h-8 text-xs mt-1" data-testid="urssaf-taux" /></label>
          <label className="text-[11px] text-neutral-500">Date d'effet du taux
            <Input type="date" value={f.eff} onChange={(e) => setF({ ...f, eff: e.target.value })} className="h-8 text-xs mt-1" data-testid="urssaf-eff" /></label>
          <label className="text-[11px] text-neutral-500">Périodicité de déclaration
            <select value={f.urssaf_periodicite} onChange={(e) => setF({ ...f, urssaf_periodicite: e.target.value })} className={`${inp} w-full mt-1`} data-testid="urssaf-periodicite">
              <option value="mensuelle">Mensuelle</option><option value="trimestrielle">Trimestrielle</option>
            </select></label>
          <label className="text-[11px] text-neutral-500">Provision impôts / taxes (€ / mois)
            <Input type="number" min="0" value={f.impots_provision_mensuelle} onChange={(e) => setF({ ...f, impots_provision_mensuelle: e.target.value })} className="h-8 text-xs mt-1" data-testid="urssaf-impots" /></label>
          <div className="col-span-2 lg:col-span-4 flex justify-end">
            <Button onClick={saveCfg} className="h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="urssaf-save">Enregistrer</Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(([l, v, sub]) => (
          <div key={l} className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`urssaf-card-${l.slice(0, 12).replace(/\W/g, "-")}`}>
            <p className="text-[11px] text-neutral-400 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" style={{ color: VIOLET }} />{l}</p>
            <p className="text-xl font-bold text-[#0B132B] mt-1">{v}</p>
            <p className="text-[10px] text-neutral-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-neutral-100 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="p-4 border-b border-neutral-100 flex flex-wrap items-end gap-2">
          <p className="text-sm font-semibold text-[#0B132B] flex-1">Cotisations déclarées / payées</p>
          <Input type="month" value={entry.period} onChange={(e) => setEntry({ ...entry, period: e.target.value })} className="h-8 text-xs w-36" data-testid="urssaf-entry-period" />
          <Input type="number" min="0" placeholder="Déclaré €" value={entry.declared} onChange={(e) => setEntry({ ...entry, declared: e.target.value })} className="h-8 text-xs w-28" data-testid="urssaf-entry-declared" />
          <Input type="number" min="0" placeholder="Payé €" value={entry.paid} onChange={(e) => setEntry({ ...entry, paid: e.target.value })} className="h-8 text-xs w-28" data-testid="urssaf-entry-paid" />
          <Button onClick={addEntry} className="h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="urssaf-entry-add"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter</Button>
        </div>
        {data.entries.length === 0 ? <p className="py-8 text-center text-xs text-neutral-400">Aucune écriture sur la période — l'estimation automatique reste indicative.</p> : (
          <table className="w-full text-xs" data-testid="urssaf-entries">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
              <th className="px-4 py-2 font-medium">Période</th><th className="px-2 py-2 font-medium text-right">Déclaré</th>
              <th className="px-2 py-2 font-medium text-right">Payé</th><th className="px-2 py-2 font-medium">Note</th><th className="px-2 py-2" /></tr></thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-b border-neutral-50" data-testid={`urssaf-entry-${e.id}`}>
                  <td className="px-4 py-2.5 capitalize">{frMonth(e.period)}</td>
                  <td className="px-2 py-2.5 text-right font-medium">{eur(e.declared)}</td>
                  <td className="px-2 py-2.5 text-right font-medium text-emerald-600">{eur(e.paid)}</td>
                  <td className="px-2 py-2.5 text-neutral-400">{e.note || "—"}</td>
                  <td className="px-2 py-2.5 text-right"><button onClick={() => del(e.id)} className="p-1 text-neutral-400 hover:text-rose-500" data-testid={`urssaf-del-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
